import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import {
  withdrawAssertion,
  type Assertion,
  type PropertyDefinition,
  type PropertySummary
} from '@peruselab/api-client/assertions';
import { Icon } from '../../../components/Icon';
import { ReferenceList } from '../../discussions/ReferenceCitation';
import { moleculePagePath, usePublicAccess } from '../../public/publicAccess';
import { AssertionProblemReport } from './AssertionProblemReport';
import { AddValueForm } from './AddValueForm';
import { conditionTexts, formatIn, formatSi, formatValue, unitSymbol } from './assertionText';
import { propertyCards, type PropertyCard } from '../../monograph/propertyCards';
import { isCurrent, type MoleculePropertiesState } from './useMoleculeProperties';

function ValueRow({
  assertion,
  property,
  catalog,
  onCorrect,
  onWithdraw,
  onChanged,
  preferred,
  focused,
  onDiscuss,
  displayUnit
}: {
  readonly assertion: Assertion;
  readonly property: PropertyDefinition | undefined;
  readonly catalog: readonly PropertyDefinition[];
  readonly onCorrect: (assertion: Assertion) => void;
  readonly onWithdraw: (assertion: Assertion) => void;
  readonly onChanged: () => Promise<void>;
  /** D9: this is the property's preferred value, and the step of the rule that chose it. */
  readonly preferred: PropertySummary['reasons'][number] | null;
  /** D16: brought into view from a comment about it. */
  readonly focused: boolean;
  readonly onDiscuss?: ((assertion: Assertion, label: string) => void) | undefined;
  /** #931: show the value in this unit (the reader's °C/°F), with the listed one beside it. */
  readonly displayUnit?: string | null | undefined;
}) {
  const { t, i18n } = useTranslation();
  const { signedIn } = usePublicAccess();
  const rowRef = useRef<HTMLLIElement | null>(null);
  useEffect(() => {
    if (focused) rowRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [focused]);
  const converted = displayUnit ? formatIn(assertion, displayUnit, property, i18n.language) : null;
  const si = converted ? null : formatSi(assertion, i18n.language);
  const anyUnit = (code: string) =>
    catalog.map((candidate) => unitSymbol(code, candidate)).find((symbol) => symbol !== code) ?? unitSymbol(code);
  const quantities = assertion.conditions
    .filter((condition) => condition.kind !== 'solvent')
    .map((condition) =>
      condition.kind === 'ph'
        ? t('properties.at.ph', { value: condition.value })
        : t('properties.at.quantity', { value: `${condition.value} ${anyUnit(condition.unit)}`.trim() })
    )
    .join(', ');
  // #920 (D5): the solvent is a Molecule -- link to its own page.
  const solvent = assertion.conditions.find((condition) => condition.kind === 'solvent');
  const history = !isCurrent(assertion);
  return (
    <li
      id={`a-${assertion.id}`}
      ref={rowRef}
      className={`assertion${history ? ' assertion--history' : ''}${focused ? ' published-statement--focused' : ''}`}
      data-testid="assertion"
    >
      <div className="assertion-line">
        <strong className="assertion-value" dir="ltr">
          {converted ?? formatValue(assertion, property)}
        </strong>
        {converted ? (
          <span className="assertion-si">{t('properties.listedAs', { value: formatValue(assertion, property) })}</span>
        ) : null}
        {si ? <span className="assertion-si">({si})</span> : null}
        {quantities || solvent ? (
          <span className="assertion-conditions">
            {quantities}
            {quantities && solvent ? ', ' : ''}
            {solvent ? (
              <>
                {t('properties.in')}{' '}
                <Link to={moleculePagePath(solvent.value, signedIn)} dir="auto">
                  {solvent.label ?? t('properties.conditions.solvent')}
                </Link>
              </>
            ) : null}
          </span>
        ) : null}
        {preferred ? (
          <span className="assertion-preferred" title={t(`properties.preferredWhy.${preferred}`)}>
            <Icon name="star" size="0.85rem" />
            <span>{t('properties.preferred')}</span>
          </span>
        ) : null}
        <span className={`assertion-standing assertion-standing--${assertion.standing.toLowerCase()}`}>
          {t(`properties.standing.${assertion.standing}`)}
        </span>
        {assertion.audience === 'PRIVATE' ? (
          <span className="comment-badge">
            <Icon name="lock" size="0.8rem" /> {t('properties.audience.PRIVATE')}
          </span>
        ) : null}
        {assertion.withdrawn ? <span className="comment-badge">{t('properties.withdrawn')}</span> : null}
        {assertion.supersededBy ? <span className="comment-badge">{t('properties.superseded')}</span> : null}
      </div>
      <p className="assertion-method">
        {t(`properties.methods.${assertion.method.kind}`)}
        {assertion.method.detail ? <span dir="auto">: {assertion.method.detail}</span> : null}
        {assertion.method.confidence != null
          ? ` ${t('properties.confidence', { percent: Math.round(assertion.method.confidence * 100) })}`
          : ''}
        {assertion.unpublished ? (
          <span>
            {' · '}
            {t('properties.unpublished')}
            {assertion.unpublishedNote ? <span dir="auto"> ({assertion.unpublishedNote})</span> : null}
          </span>
        ) : null}
        {assertion.imported ? (
          <span> · {t('properties.importedFrom', { importer: assertion.imported.importer })}</span>
        ) : assertion.authorName ? (
          <span> · {assertion.authorName}</span>
        ) : null}
      </p>
      {assertion.imported ? (
        // #920 (D14): the source's own words, so nothing is lost in the reading.
        <p className="assertion-import">
          {t('properties.asListed')} <q dir="auto">{assertion.imported.reportedAs}</q>
          {assertion.imported.citation ? (
            <span>
              {' · '}
              {t('properties.cites')} <span dir="auto">{assertion.imported.citation}</span>
            </span>
          ) : null}
        </p>
      ) : null}
      <ReferenceList references={assertion.references} />
      <AssertionProblemReport assertion={assertion} onChanged={onChanged} />
      {preferred ? <p className="visually-hidden">{t(`properties.preferredWhy.${preferred}`)}</p> : null}
      {(assertion.mine && !history) || (onDiscuss && !history) ? (
        <div className="assertion-actions">
          {onDiscuss ? (
            <button
              type="button"
              className="icon-button"
              onClick={() =>
                onDiscuss(assertion, `${property?.name ?? assertion.propertyId}: ${formatValue(assertion, property)}`)
              }
              aria-label={t('moleculePage.discuss')}
              title={t('moleculePage.discuss')}
            >
              <Icon name="forum" />
            </button>
          ) : null}
        </div>
      ) : null}
      {assertion.mine && !history ? (
        <div className="assertion-actions">
          <button
            type="button"
            className="icon-button"
            onClick={() => onCorrect(assertion)}
            aria-label={t('properties.correct')}
            title={t('properties.correct')}
          >
            <Icon name="edit" />
          </button>
          <button
            type="button"
            className="icon-button"
            onClick={() => {
              if (globalThis.confirm(t('properties.confirmWithdraw'))) onWithdraw(assertion);
            }}
            aria-label={t('properties.withdraw')}
            title={t('properties.withdraw')}
          >
            <Icon name="delete" />
          </button>
        </div>
      ) : null}
    </li>
  );
}

/**
 * ADR 0018 phase 1 (#915): the factsheet's Properties -- every reported value for each property,
 * side by side and never merged (principle 2), each with its conditions, method, standing and
 * sources (D8); history (superseded, withdrawn) on request (D13); and "Add a value".
 */
export function PropertiesSection({
  moleculeId,
  properties,
  onDiscuss,
  focusAssertionId,
  focusPropertyId,
  addRequest = 0,
  layout = 'list',
  propertyId,
  displayUnit,
  onAdded
}: {
  readonly moleculeId: string;
  /** #929: loaded once for the Monograph (useMoleculeProperties), shared with the property cards. */
  readonly properties: MoleculePropertiesState;
  /** #920 (D16): "Discuss" on a value starts an anchored comment in the molecule's thread. */
  readonly onDiscuss?: (assertion: Assertion, label: string) => void;
  /** #920: the value to bring into view (a comment about it was tapped, or the address names it). */
  readonly focusAssertionId?: string | null;
  /** #929: the property a card was tapped for -- brought into view. */
  readonly focusPropertyId?: string | null;
  /** #929: each increase opens the add-value form ("Add what you know", the dashed card). */
  readonly addRequest?: number;
  /** #930: 'table' (the Sheet view) -- one row per property, expanding to every value; 'list' shows them all. */
  readonly layout?: 'list' | 'table' | 'evidence';
  /** #931 (evidence layout): the one property shown. */
  readonly propertyId?: string;
  /** #931 (evidence layout): the reader's display unit (°C/°F), or null for each value's own. */
  readonly displayUnit?: string | null;
  /** #931: a value was added -- the evidence view turns to its property. */
  readonly onAdded?: ((propertyId: string) => void) | undefined;
}) {
  const { t, i18n } = useTranslation();
  const headingId = useId();
  const { signedIn, signIn } = usePublicAccess();
  const location = useLocation();
  const { loaded, failed, reload: load } = properties;
  const [adding, setAdding] = useState(false);
  const [correcting, setCorrecting] = useState<Assertion | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const expand = (propertyId: string) => setExpanded((open) => new Set(open).add(propertyId));
  const toggle = (propertyId: string) =>
    setExpanded((open) => {
      const next = new Set(open);
      if (next.has(propertyId)) next.delete(propertyId);
      else next.add(propertyId);
      return next;
    });

  useEffect(() => {
    if (addRequest > 0 && signedIn) setAdding(true);
  }, [addRequest, signedIn]);

  // #930: a property or value brought into view opens its row in the table.
  const focusedPropertyOfValue = loaded?.assertions.find((assertion) => assertion.id === focusAssertionId)?.propertyId;
  useEffect(() => {
    if (focusPropertyId) expand(focusPropertyId);
  }, [focusPropertyId]);
  useEffect(() => {
    if (focusedPropertyOfValue) expand(focusedPropertyOfValue);
  }, [focusedPropertyOfValue]);

  useEffect(() => {
    if (!focusPropertyId) return;
    document.getElementById(`p-${focusPropertyId}`)?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [focusPropertyId, loaded]);

  // A value brought into view that's only in the history (corrected or withdrawn since): show the history.
  const focusInHistory = Boolean(
    focusAssertionId &&
    loaded?.assertions.some((assertion) => assertion.id === focusAssertionId && !isCurrent(assertion))
  );
  useEffect(() => {
    if (focusInHistory) setShowHistory(true);
  }, [focusInHistory]);

  if (failed) return null;
  if (!loaded) return null;
  const { catalog, assertions, summaries } = loaded;
  const summaryOf = (propertyId: string) => summaries.find((summary) => summary.propertyId === propertyId);
  const number = new Intl.NumberFormat(i18n.language, { maximumSignificantDigits: 6 });
  const shown = assertions.filter((assertion) => showHistory || isCurrent(assertion));
  const historyCount = assertions.length - assertions.filter(isCurrent).length;
  const byProperty = catalog
    .map((property) => ({ property, values: shown.filter((assertion) => assertion.propertyId === property.id) }))
    .filter(({ values }) => values.length > 0);

  const valueList = (property: PropertyDefinition, values: readonly Assertion[], trailing?: ReactNode) => (
    <ul className={layout === 'evidence' ? 'assertions assertions--cards' : 'assertions'}>
      {values.map((assertion) => (
        <ValueRow
          key={assertion.id}
          onChanged={properties.reload}
          assertion={assertion}
          property={property}
          catalog={catalog}
          onCorrect={(chosen) => {
            setCorrecting(chosen);
            setAdding(true);
          }}
          onWithdraw={(chosen) => void withdrawAssertion(chosen.id).then(load)}
          preferred={
            !assertion.problemReport &&
            summaryOf(property.id)?.preferredId === assertion.id &&
            (summaryOf(property.id)?.count ?? 0) > 1
              ? (summaryOf(property.id)?.reasons[0] ?? null)
              : null
          }
          focused={focusAssertionId === assertion.id}
          onDiscuss={onDiscuss}
          displayUnit={displayUnit}
        />
      ))}
      {trailing}
    </ul>
  );
  const cards = new Map<string, PropertyCard>(propertyCards(loaded).map((card) => [card.property.id, card]));
  const addButton = adding ? null : signedIn ? (
    <button
      type="button"
      className="discussion-start icon-button"
      onClick={() => setAdding(true)}
      aria-label={t('properties.add')}
      title={t('properties.add')}
    >
      <Icon name="add" />
    </button>
  ) : (
    <button
      type="button"
      className="discussion-start icon-button"
      onClick={() => signIn(location.pathname + location.search)}
      aria-label={t('properties.signInToAdd')}
      title={t('properties.signInToAdd')}
    >
      <Icon name="lock" />
    </button>
  );

  const addForm = adding ? (
    <AddValueForm
      moleculeId={moleculeId}
      catalog={catalog}
      correcting={correcting}
      initialPropertyId={layout === 'evidence' ? propertyId : undefined}
      onDone={(created) => {
        setAdding(false);
        setCorrecting(null);
        onAdded?.(created.propertyId);
        void load();
      }}
      onCancel={() => {
        setAdding(false);
        setCorrecting(null);
      }}
    />
  ) : null;

  if (layout === 'evidence') {
    // #931: one property's values as cards, every one with its source, then "Add a value with its source".
    const property = catalog.find((candidate) => candidate.id === propertyId);
    if (!property) return null;
    const values = shown.filter((assertion) => assertion.propertyId === property.id);
    const current = values.filter(isCurrent).length;
    const history = assertions.filter(
      (assertion) => assertion.propertyId === property.id && !isCurrent(assertion)
    ).length;
    return (
      <section className="properties-section properties-section--evidence" aria-labelledby={headingId}>
        <div className="monograph-section-heading">
          <h2 id={headingId}>{t('properties.evidence.heading', { count: current })}</h2>
          <p>{t('properties.evidence.hint')}</p>
        </div>
        {addForm}
        {current === 0 && byProperty.length === 0 ? <p className="discussion-note">{t('properties.none')}</p> : null}
        {valueList(
          property,
          values,
          adding ? null : (
            <li className="assertion-add-card">
              <button
                type="button"
                onClick={() => (signedIn ? setAdding(true) : signIn(location.pathname + location.search))}
              >
                <Icon name={signedIn ? 'add' : 'lock'} size="1.4rem" />
                <span>{signedIn ? t('properties.evidence.add') : t('properties.signInToAdd')}</span>
              </button>
            </li>
          )
        )}
        {history > 0 ? (
          <button
            type="button"
            className="discussion-link-button"
            aria-expanded={showHistory}
            onClick={() => setShowHistory(!showHistory)}
          >
            {showHistory ? t('properties.hideHistory') : t('properties.showHistory', { count: history })}
          </button>
        ) : null}
      </section>
    );
  }

  if (layout === 'table') {
    return (
      <section
        className="published-facts-section properties-section properties-section--table"
        aria-labelledby={headingId}
      >
        <div className="properties-table-heading">
          <h2 id={headingId}>{t('properties.heading')}</h2>
          {addButton}
        </div>
        {adding ? (
          <AddValueForm
            moleculeId={moleculeId}
            catalog={catalog}
            correcting={correcting}
            onDone={() => {
              setAdding(false);
              setCorrecting(null);
              void load();
            }}
            onCancel={() => {
              setAdding(false);
              setCorrecting(null);
            }}
          />
        ) : null}
        {byProperty.length === 0 ? <p className="discussion-note">{t('properties.none')}</p> : null}
        {byProperty.length > 0 ? (
          <div className="properties-table-scroll">
            <table className="properties-table">
              <thead>
                <tr>
                  <th scope="col">{t('properties.table.property')}</th>
                  <th scope="col">{t('properties.table.preferred')}</th>
                  <th scope="col">{t('properties.table.conditions')}</th>
                  <th scope="col">{t('properties.table.evidence')}</th>
                  <th scope="col">{t('properties.table.reported')}</th>
                </tr>
              </thead>
              {byProperty.map(({ property, values }) => {
                const card = cards.get(property.id);
                const open = expanded.has(property.id);
                const rowsId = `${headingId}-${property.id}`;
                const conditions = card?.preferred ? conditionTexts(card.preferred, catalog) : null;
                const method = card?.preferred?.method.kind;
                return (
                  <tbody key={property.id} id={`p-${property.id}`} className={open ? 'open' : ''}>
                    <tr>
                      <th scope="row">
                        <button
                          type="button"
                          className="properties-table-toggle"
                          aria-expanded={open}
                          aria-controls={rowsId}
                          onClick={() => toggle(property.id)}
                        >
                          <Icon name={open ? 'expandMore' : 'chevronRight'} size="1.1rem" />
                          <span>{property.name}</span>
                        </button>
                      </th>
                      <td className="properties-table-value" dir="ltr">
                        {card?.preferred
                          ? formatValue(card.preferred, property)
                          : card
                            ? t('properties.report.noPreferred')
                            : '—'}
                      </td>
                      <td>
                        {conditions && (conditions.quantities.length > 0 || conditions.solvent)
                          ? [
                              ...conditions.quantities.map((text) => t('properties.at.quantity', { value: text })),
                              ...(conditions.solvent ? [`${t('properties.in')} ${conditions.solvent}`] : [])
                            ].join(', ')
                          : '—'}
                      </td>
                      <td>
                        {method === 'CALCULATED' || method === 'PREDICTED' ? (
                          <span className="monograph-chip monograph-chip--warn">{t(`monograph.method.${method}`)}</span>
                        ) : card && card.sourced > 0 ? (
                          <span className="monograph-chip">{t('monograph.sourced', { count: card.sourced })}</span>
                        ) : card ? (
                          <span className="monograph-chip monograph-chip--plain">
                            {card.preferred
                              ? t(`properties.standing.${card.preferred.standing}`)
                              : t('properties.report.badge')}
                          </span>
                        ) : null}
                      </td>
                      <td>
                        {card
                          ? card.agreement === 'agree'
                            ? t('properties.table.allAgree', { count: card.count })
                            : t('monograph.values', { count: card.count })
                          : t('monograph.values', { count: values.length })}
                      </td>
                    </tr>
                    {open ? (
                      <tr className="properties-table-values">
                        <td colSpan={5} id={rowsId}>
                          {valueList(property, values)}
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                );
              })}
            </table>
          </div>
        ) : null}
        {historyCount > 0 ? (
          <button
            type="button"
            className="discussion-link-button"
            aria-expanded={showHistory}
            onClick={() => setShowHistory(!showHistory)}
          >
            {showHistory ? t('properties.hideHistory') : t('properties.showHistory', { count: historyCount })}
          </button>
        ) : null}
      </section>
    );
  }

  return (
    <section className="published-facts-section properties-section" aria-labelledby={headingId}>
      <h2 id={headingId}>{t('properties.heading')}</h2>
      {byProperty.length === 0 ? <p className="discussion-note">{t('properties.none')}</p> : null}
      <dl className="properties-list">
        {byProperty.map(({ property, values }) => (
          <div key={property.id} id={`p-${property.id}`} className="properties-entry">
            <dt>
              {property.name}
              {(() => {
                // D9: how many, and their range in the unit most of them used.
                const summary = summaryOf(property.id);
                if (!summary || summary.count < 2) return null;
                const symbol = unitSymbol(summary.displayUnit, property);
                const range =
                  summary.displayMin !== null &&
                  summary.displayMax !== null &&
                  summary.displayMin !== summary.displayMax
                    ? `${number.format(summary.displayMin)}–${number.format(summary.displayMax)}${symbol ? ` ${symbol}` : ''}`
                    : null;
                return (
                  <span className="comment-meta">
                    {' · '}
                    {t('properties.reported', { count: summary.count })}
                    {range ? <span dir="ltr"> · {range}</span> : null}
                  </span>
                );
              })()}
            </dt>
            <dd>{valueList(property, values)}</dd>
          </div>
        ))}
      </dl>
      {historyCount > 0 ? (
        <button
          type="button"
          className="discussion-link-button"
          aria-expanded={showHistory}
          onClick={() => setShowHistory(!showHistory)}
        >
          {showHistory ? t('properties.hideHistory') : t('properties.showHistory', { count: historyCount })}
        </button>
      ) : null}
      {adding ? (
        <AddValueForm
          moleculeId={moleculeId}
          catalog={catalog}
          correcting={correcting}
          onDone={() => {
            setAdding(false);
            setCorrecting(null);
            void load();
          }}
          onCancel={() => {
            setAdding(false);
            setCorrecting(null);
          }}
        />
      ) : signedIn ? (
        <button
          type="button"
          className="discussion-start icon-button"
          onClick={() => setAdding(true)}
          aria-label={t('properties.add')}
          title={t('properties.add')}
        >
          <Icon name="add" />
        </button>
      ) : (
        <button
          type="button"
          className="discussion-start icon-button"
          onClick={() => signIn(location.pathname + location.search)}
          aria-label={t('properties.signInToAdd')}
          title={t('properties.signInToAdd')}
        >
          <Icon name="lock" />
        </button>
      )}
    </section>
  );
}
